import { useToast } from '@/components/ui/Toast'
import { useQuery } from '@tanstack/react-query'
import { usersApi } from '../api/usersApi'

/**
 * Query keys for users
 */
export const usersKeys = {
  all: ['users'],
  lists: () => [...usersKeys.all, 'list'],
  list: (filters) => [...usersKeys.lists(), filters],
  details: () => [...usersKeys.all, 'detail'],
  detail: (id) => [...usersKeys.details(), id],
}

/**
 * Hook to get users list
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result
 */
export function useUsers(params = {}) {
  const toast = useToast()

  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: () => usersApi.getUsers(params),
    onError: (error) => {
      if (error.response?.status === 403) {
        toast.error({
          title: 'Access Denied',
          description: 'You do not have permission to view users list.',
        })
      } else {
        toast.error({
          title: 'Failed to load users',
          description: error.message || 'Something went wrong.',
        })
      }
    },
  })
}

/**
 * Hook to get single user by ID
 * @param {number} id - User ID
 * @returns {Object} React Query result
 */
export function useUser(id) {
  const toast = useToast()

  return useQuery({
    queryKey: usersKeys.detail(id),
    queryFn: () => usersApi.getUserById(id),
    enabled: !!id,
    onError: (error) => {
      if (error.response?.status === 403) {
        toast.error({
          title: 'Access Denied',
          description: 'You do not have permission to view this user.',
        })
      } else if (error.response?.status === 404) {
        toast.error({
          title: 'User Not Found',
          description: 'The requested user does not exist.',
        })
      } else {
        toast.error({
          title: 'Failed to load user',
          description: error.message || 'Something went wrong.',
        })
      }
    },
  })
}
